import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { db, auth } from '../../firebaseConfig';
import { collection, getDocs, doc, getDoc, query, where, writeBatch, serverTimestamp, updateDoc } from 'firebase/firestore';
import Select from 'react-select';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import ScaleManagerModal from './ScaleManagerModal';

// --- BIBLIOTECAS PARA DRAG AND DROP E ÍCONES ---
import { DndContext, closestCenter } from '@dnd-kit/core';
import { arrayMove, SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { FaGripVertical, FaTrashAlt, FaUserCheck, FaUserTie, FaUsers, FaSitemap, FaArrowUp, FaArrowDown, FaThList, FaThLarge } from 'react-icons/fa';

import '../../styles/CriarAvaliacao.css';

// --- CONFIGURAÇÕES E CONSTANTES ---
const methodologyOptions = [
    { value: '360', label: '360° (Gestor, Pares, Liderados e Autoavaliação)' },
    { value: '180', label: '180° (Gestor e Autoavaliação)' },
    { value: 'peers', label: 'Avaliação entre Pares' },
    { value: 'self', label: 'Apenas Autoavaliação' }
];

const nineBoxAxisOptions = [
    { value: 'none', label: 'Não se aplica ao 9-Box' },
    { value: 'performance', label: 'Eixo de Desempenho' },
    { value: 'potential', label: 'Eixo de Potencial' }
];

// Estilo unificado para garantir alinhamento e whitelabel
const sharedSelectStyles = {
    control: (provided, state) => ({
        ...provided,
        height: 'auto',
        minHeight: '45px',
        border: '1px solid var(--input-border-color, #ced4da)',
        borderRadius: '8px',
        backgroundColor: state.isDisabled ? '#e9ecef' : '#f8f9fa',
        borderColor: state.isFocused ? 'var(--primary-color, #007bff)' : 'var(--input-border-color, #ced4da)',
        boxShadow: state.isFocused ? `0 0 0 1px var(--primary-color, #007bff)` : 'none',
        '&:hover': {
            borderColor: state.isFocused ? 'var(--primary-color, #007bff)' : '#adb5bd'
        }
    }),
    valueContainer: (provided) => ({ ...provided, padding: '2px 8px' }),
    singleValue: (provided) => ({ ...provided, whiteSpace: 'normal', color: '#212529' }),
    option: (provided, state) => ({
        ...provided,
        backgroundColor: state.isSelected ? 'var(--primary-color, #007bff)' : state.isFocused ? '#e9ecef' : 'white',
        color: state.isSelected ? 'white' : '#333',
        padding: '10px 15px'
    }),
    menuPortal: base => ({ ...base, zIndex: 9999 })
};

const cardSelectStyles = {
    ...sharedSelectStyles,
    control: (provided, state) => ({
        ...sharedSelectStyles.control(provided, state),
        backgroundColor: '#fff',
    }),
};

// --- COMPONENTES AUXILIARES ---
const Notification = ({ message, type, onClose }) => {
    if (!message) return null;
    return (<div className={`notification ${type}`}><span>{message}</span><button onClick={onClose}>&times;</button></div>);
};

function SortableQuestionCard({ q, index, handleQuestionChange, handleWeightChange, deleteQuestion, availableScales, departmentOptions, handleMoveQuestion, isFirst, isLast, is9BoxEnabled }) {
    const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: q.id });
    const style = { transform: CSS.Transform.toString(transform), transition };

    return (
        <div ref={setNodeRef} style={style} className="question-card">
            <div className="question-card-main">
                <span className="drag-handle" {...attributes} {...listeners} title="Reordenar Pergunta"><FaGripVertical /></span>
                <div className="question-input-wrapper">
                    <label className="option-label">Pergunta {index + 1}</label>
                    <input type="text" value={q.text} onChange={(e) => handleQuestionChange(q.id, 'text', e.target.value)} placeholder="Escreva o texto da pergunta aqui..." className="question-text-input"/>
                </div>
            </div>
            <div className="question-card-footer">
                <div className="footer-options">
                    {is9BoxEnabled && (<div className="option-group"><label className="option-label">Eixo 9-Box</label><Select options={nineBoxAxisOptions} value={nineBoxAxisOptions.find(opt => opt.value === q.axis) || null} onChange={(selectedOption) => handleQuestionChange(q.id, 'axis', selectedOption.value)} styles={cardSelectStyles} /></div>)}
                    <div className="option-group department-group"><label className="option-label">Departamento (Opcional)</label><Select isMulti options={departmentOptions} value={q.departments} onChange={(options) => handleQuestionChange(q.id, 'departments', options)} placeholder="Todos" menuPortalTarget={document.body} styles={cardSelectStyles} /></div>
                    <div className="option-group"><label className="option-label">Tipo de Escala</label><Select options={availableScales} value={availableScales.find(scale => scale.value === q.type)} onChange={(selectedOption) => handleQuestionChange(q.id, 'type', selectedOption.value)} styles={cardSelectStyles} menuPortalTarget={document.body} /></div>
                    <div className="option-group"><label className="option-label">Peso</label><div className="custom-stepper"><button type="button" onClick={() => handleWeightChange(q.id, -0.5)}>-</button><input type="number" value={q.weight} onChange={(e) => handleQuestionChange(q.id, 'weight', e.target.value)} className="stepper-input" min="0.5" step="0.5"/><button type="button" onClick={() => handleWeightChange(q.id, 0.5)}>+</button></div></div>
                </div>
                <div className="footer-actions">
                     <div className="justification-checkbox"><input type="checkbox" id={`justification-${q.id}`} checked={q.isJustificationMandatory} onChange={(e) => handleQuestionChange(q.id, 'isJustificationMandatory', e.target.checked)}/><label htmlFor={`justification-${q.id}`}>Justificativa obrigatória?</label></div>
                    <div className="reorder-buttons"><button onClick={() => handleMoveQuestion(q.id, 'up')} disabled={isFirst} className="icon-btn" title="Mover para Cima"><FaArrowUp /></button><button onClick={() => handleMoveQuestion(q.id, 'down')} disabled={isLast} className="icon-btn" title="Mover para Baixo"><FaArrowDown /></button></div>
                    <button onClick={() => deleteQuestion(q.id)} className="icon-btn delete-btn" title="Excluir Pergunta"><FaTrashAlt /></button>
                </div>
            </div>
        </div>
    );
}

// --- COMPONENTE PRINCIPAL ---
const CriarAvaliacao = () => {
    const navigate = useNavigate();
    const { cycleId } = useParams();
    const location = useLocation();
    const cycleNameRef = useRef(null), datesRef = useRef(null), evaluatedUsersRef = useRef(null), formQuestionsRef = useRef(null);
    
    // --- State ---
    const [mode, setMode] = useState('create');
    const [pageTitle, setPageTitle] = useState('Criar Novo Ciclo de Avaliação');
    const [cycleName, setCycleName] = useState('');
    const [methodology, setMethodology] = useState('360');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [baseScale, setBaseScale] = useState(10);
    const [is9BoxEnabled, setIs9BoxEnabled] = useState(true);
    const [allUsers, setAllUsers] = useState([]);
    const [departmentOptions, setDepartmentOptions] = useState([]);
    const [evaluatedUsers, setEvaluatedUsers] = useState([]);
    const [finalEvaluatorsConfig, setFinalEvaluatorsConfig] = useState({});
    const [formQuestions, setFormQuestions] = useState([]);
    const [availableScales, setAvailableScales] = useState([]);
    const [showScaleManager, setShowScaleManager] = useState(false);
    const [loading, setLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [loadingSuggestions, setLoadingSuggestions] = useState(false);
    const [adminEmpresaId, setAdminEmpresaId] = useState(null);
    const [notification, setNotification] = useState({ message: '', type: '' });
    const [errors, setErrors] = useState({});
    const [departmentFilter, setDepartmentFilter] = useState(null);
    const [viewMode, setViewMode] = useState('card');
    const [cycleStatus, setCycleStatus] = useState('draft');
    const [relationshipWeights, setRelationshipWeights] = useState({ self: 1, manager: 1, peers: 1, direct_report: 1 });
    // NOVO ESTADO PARA VISIBILIDADE
    const [resultsVisibility, setResultsVisibility] = useState({
        showFinalScore: true,
        showManagerScore: true,
        showPeerScore: true,
        showSelfScore: true,
        showManagerComments: true,
        showPeerComments: true,
    });

    // --- Funções ---
    const showNotification = (message, type = 'error') => { setNotification({ message, type }); setTimeout(() => setNotification({ message: '', type: '' }), 5000); };
    const formatDateForInput = (date) => date ? (date.toDate ? date.toDate() : new Date(date)).toISOString().split('T')[0] : '';
    // NOVA FUNÇÃO HANDLER PARA VISIBILIDADE
    const handleVisibilityChange = (e) => {
        const { id, checked } = e.target;
        setResultsVisibility(prev => ({ ...prev, [id]: checked }));
    };

    const handleWeightChangeForRelationship = (relationship, value) => {
        const numericValue = Number(value);
        if (numericValue >= 0) {
            setRelationshipWeights(prev => ({ ...prev, [relationship]: numericValue }));
        }
    };

    useEffect(() => {
        if (cycleId) {
            if (location.pathname.includes('/editar')) { setMode('edit'); setPageTitle('Editar Ciclo de Avaliação'); } 
            else if (location.pathname.includes('/duplicar')) { setMode('duplicate'); setPageTitle('Duplicar Ciclo de Avaliação'); }
        } else { setMode('create'); setPageTitle('Criar Novo Ciclo de Avaliação'); }
    }, [cycleId, location.pathname]);

    const addQuestion = useCallback(() => {
        const newId = `q_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        setFormQuestions(prev => [...prev, { id: newId, questionId: newId, text: '', type: 'default_0_5', weight: 1, isJustificationMandatory: false, departments: [], axis: 'none' }]);
    }, []);

    const deleteQuestion = (id) => {
        if (formQuestions.length > 1) { setFormQuestions(prev => prev.filter(q => q.id !== id)); } 
        else { showNotification("O formulário precisa ter pelo menos uma pergunta."); }
    };

    const handleMoveQuestion = (questionId, direction) => {
        const currentIndex = formQuestions.findIndex(q => q.id === questionId);
        if (currentIndex === -1) return;
        const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
        if (newIndex >= 0 && newIndex < formQuestions.length) setFormQuestions(arrayMove(formQuestions, currentIndex, newIndex));
    };

    const fetchScales = useCallback(async (empresaId) => {
        if (!empresaId) return;
        const scalesRef = collection(db, 'Empresas', empresaId, 'evaluationScales');
        const snapshot = await getDocs(query(scalesRef));
        const customScales = snapshot.docs.map(doc => ({ value: doc.id, label: `${doc.data().name} (${doc.data().min} a ${doc.data().max})` }));
        setAvailableScales([{ value: 'default_0_5', label: 'Padrão com Emojis (1 a 5)' }, { value: 'default_0_10', label: 'Padrão Numérica (0 a 10)' }, ...customScales]);
    }, []);

    const fetchAdminDataAndUsers = useCallback(async () => {
        const user = auth.currentUser;
        if (!user) { navigate('/login'); return {}; }
        const userDocSnap = await getDoc(doc(db, 'users', user.uid));
        if (userDocSnap.exists() && userDocSnap.data().empresa_id) {
            const empresaId = userDocSnap.data().empresa_id;
            setAdminEmpresaId(empresaId);
            await fetchScales(empresaId);
            const usersRef = collection(db, 'users');
            const q = query(usersRef, where("empresa_id", "==", empresaId));
            const snapshot = await getDocs(q);
            const userList = snapshot.docs.map(d => ({ ...d.data(), id: d.id, value: d.id, label: `${d.data().name} (${d.data().departamento || 'Sem Depto.'})` }));
            setAllUsers(userList);
            if (userList.length > 0) {
                const depts = userList.map(u => u.departamento).filter(Boolean);
                const uniqueDepts = [...new Set(depts)].sort();
                setDepartmentOptions([{ value: 'all', label: 'Todos os Departamentos' }, ...uniqueDepts.map(d => ({ value: d, label: d }))]);
            }
            return { empresaId, userList };
        } else {
            showNotification("ID da empresa não configurado para seu usuário.");
            return {};
        }
    }, [navigate, fetchScales]);

    const calculateEvaluatorsFromOrgChart = useCallback(async (selectedUsers, empresaId) => {
        if (!empresaId || selectedUsers.length === 0) return {};
        const nodesRef = collection(db, 'Empresas', empresaId, 'organizationChartNodes');
        const nodesSnapshot = await getDocs(nodesRef);
        const allNodes = nodesSnapshot.docs.map(d => ({ id: d.id, ...d.data() }));
        let config = {};
        for (const user of selectedUsers) {
            const evaluatedNode = allNodes.find(node => node.userId === user.value);
            if (!evaluatedNode) { config[user.value] = { manager: [], peers: [], reports: [] }; continue; }
            let managerIds = [];
            if (evaluatedNode.parentId) {
                const managerNode = allNodes.find(node => node.id === evaluatedNode.parentId);
                if(managerNode && managerNode.userId) managerIds.push(managerNode.userId);
            }
            const reportIds = allNodes.filter(node => node.parentId === evaluatedNode.id && node.userId).map(r => r.userId);
            const peerIds = allNodes.filter(node => node.parentId === evaluatedNode.parentId && node.id !== evaluatedNode.id && node.userId).map(p => p.userId);
            config[user.value] = { manager: managerIds, peers: peerIds, reports: reportIds };
        }
        return config;
    }, []);

    useEffect(() => {
        const loadCycleData = async () => {
            const { empresaId, userList } = await fetchAdminDataAndUsers();
            if (!empresaId || !cycleId || !userList) { if (formQuestions.length === 0) addQuestion(); setLoading(false); return; }
            const cycleDocRef = doc(db, 'Empresas', empresaId, 'evaluationCycles', cycleId);
            const cycleSnap = await getDoc(cycleDocRef);
            if (cycleSnap.exists()) {
                const data = cycleSnap.data();
                setCycleStatus(data.status || 'draft');
                setMethodology(data.methodology);
                setBaseScale(data.baseScale);
                setIs9BoxEnabled(data.is9BoxEnabled || false);
                setRelationshipWeights(data.relationshipWeights || { self: 1, manager: 1, peers: 1, direct_report: 1 });
                // ATUALIZAÇÃO PARA CARREGAR DADOS DE VISIBILIDADE
                const defaultVisibility = { showFinalScore: true, showManagerScore: true, showPeerScore: true, showSelfScore: true, showManagerComments: true, showPeerComments: true };
                setResultsVisibility(data.resultsVisibility || defaultVisibility);

                const questionsWithClientData = data.form.map((q, index) => ({ ...q, id: q.questionId || `q-${index}`, departments: q.departments || [], axis: q.axis || 'none' }));
                setFormQuestions(questionsWithClientData);
                if (mode === 'edit') {
                    setCycleName(data.name);
                    setStartDate(formatDateForInput(data.startDate));
                    setEndDate(formatDateForInput(data.endDate));
                } else { setCycleName(`Cópia de ${data.name}`); }

                if (data.evaluatedUserIds) {
                    const selectedUsers = userList.filter(u => data.evaluatedUserIds.includes(u.id));
                    setEvaluatedUsers(selectedUsers);
                    setFinalEvaluatorsConfig(data.finalEvaluatorsConfig || {});
                } else {
                    const participantsRef = collection(cycleDocRef, 'participants');
                    const participantsSnap = await getDocs(participantsRef);
                    const participantIds = participantsSnap.docs.map(p => p.data().userId);
                    const selectedUsers = userList.filter(u => participantIds.includes(u.id));
                    await handleEvaluatedUsersChange(selectedUsers, empresaId);
                }
            } else {
                showNotification("Ciclo de avaliação não encontrado.");
                navigate('/avaliacoes');
            }
            setLoading(false);
        };
        if (mode === 'create') {
            fetchAdminDataAndUsers().then(() => { if (formQuestions.length === 0) addQuestion(); setLoading(false); });
        } else { loadCycleData(); }
    }, [cycleId, mode, navigate, addQuestion, fetchAdminDataAndUsers]);
    
    const handleEvaluatedUsersChange = async (selectedOptions, currentEmpresaId) => {
        const users = selectedOptions || [];
        setEvaluatedUsers(users);
        const finalEmpresaId = currentEmpresaId || adminEmpresaId;
        if (users.length > 0 && finalEmpresaId) {
            setLoadingSuggestions(true);
            const config = await calculateEvaluatorsFromOrgChart(users, finalEmpresaId);
            setFinalEvaluatorsConfig(config);
            setLoadingSuggestions(false);
        } else { setFinalEvaluatorsConfig({}); }
    };

    const handleEvaluatorChange = (evaluatedUserId, relationship, selectedEvaluators) => {
        setFinalEvaluatorsConfig(prev => ({ ...prev, [evaluatedUserId]: { ...prev[evaluatedUserId], [relationship]: selectedEvaluators.map(s => s.value) } }));
    };

    const handleQuestionChange = (id, field, value) => {
        setFormQuestions(prev => prev.map(q => q.id === id ? { ...q, [field]: value } : q));
    };
    
    const handleWeightChange = (id, amount) => {
        setFormQuestions(prev => prev.map(q => (q.id === id ? { ...q, weight: Math.max(0.5, (Number(q.weight) || 0) + amount) } : q)));
    };

    const handleBaseScaleChange = (amount) => setBaseScale(prev => Math.max(1, (Number(prev) || 0) + amount));

    const handleSaveCycle = async (newStatus) => {
        const newErrors = {};
        if (!cycleName.trim()) newErrors.cycleName = "O nome do ciclo é obrigatório.";
        
        if (newStatus === 'active') {
            if (!startDate) newErrors.startDate = "A data de início é obrigatória.";
            if (!endDate) newErrors.endDate = "A data de fim é obrigatória.";
            if (evaluatedUsers.length === 0) newErrors.evaluatedUsers = "Selecione pelo menos um colaborador.";
            const firstEmptyQuestionIndex = formQuestions.findIndex(q => q.text.trim() === '');
            if (firstEmptyQuestionIndex !== -1) newErrors.formQuestions = `A pergunta ${firstEmptyQuestionIndex + 1} não foi preenchida.`;
        }
        
        setErrors(newErrors);
        if (Object.keys(newErrors).length > 0) {
            showNotification("Por favor, corrija os campos obrigatórios indicados em vermelho.");
            const errorRefMap = { cycleName: cycleNameRef, startDate: datesRef, endDate: datesRef, evaluatedUsers: evaluatedUsersRef, formQuestions: formQuestionsRef };
            errorRefMap[Object.keys(newErrors)[0]]?.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
            return;
        }

        setIsSaving(true);
        const questionsToSave = formQuestions.map(({ id, ...rest }) => rest);
        const evaluatedUserIds = evaluatedUsers.map(user => user.value);
        // ATUALIZAÇÃO PARA SALVAR DADOS DE VISIBILIDADE
        const cycleData = { 
            name: cycleName, 
            methodology, 
            baseScale: Number(baseScale), 
            startDate: startDate ? new Date(startDate) : null, 
            endDate: endDate ? new Date(endDate) : null, 
            form: questionsToSave, 
            is9BoxEnabled, 
            status: newStatus, 
            evaluatedUserIds, 
            finalEvaluatorsConfig, 
            relationshipWeights,
            resultsVisibility // ADICIONADO AQUI
        };

        try {
            const isUpdateOperation = mode === 'edit';
            const isLaunchingDraft = mode === 'edit' && cycleStatus === 'draft' && newStatus === 'active';
            let successMessage = '';

            if (isUpdateOperation && !isLaunchingDraft) {
                const cycleRef = doc(db, 'Empresas', adminEmpresaId, 'evaluationCycles', cycleId);
                await updateDoc(cycleRef, { ...cycleData, updatedAt: serverTimestamp() });
                successMessage = 'Ciclo atualizado com sucesso!';
            } else { 
                const batch = writeBatch(db);
                const finalCycleRef = (mode === 'create' || mode === 'duplicate') 
                    ? doc(collection(db, 'Empresas', adminEmpresaId, 'evaluationCycles')) 
                    : doc(db, 'Empresas', adminEmpresaId, 'evaluationCycles', cycleId);

                if (mode === 'create' || mode === 'duplicate') {
                    batch.set(finalCycleRef, { ...cycleData, createdAt: serverTimestamp(), createdBy: auth.currentUser.uid });
                } else {
                    batch.update(finalCycleRef, { ...cycleData, updatedAt: serverTimestamp() });
                }

                if (newStatus === 'active') {
                    for (const user of evaluatedUsers) {
                        const evaluatedId = user.value;
                        const participantRef = doc(collection(finalCycleRef, 'participants'));
                        batch.set(participantRef, { userId: evaluatedId, userName: user.label, status: 'pending', finalScore: null, empresaId: adminEmpresaId });
                        
                        const config = finalEvaluatorsConfig[evaluatedId] || {};
                        const evaluators = new Map();
                        if(['self', '180', '360'].includes(methodology)) evaluators.set(evaluatedId, 'self');
                        if (['180','360'].includes(methodology)) config.manager?.forEach(id => evaluators.set(id, 'manager'));
                        if (['peers','360'].includes(methodology)) config.peers?.forEach(id => evaluators.set(id, 'peer'));
                        if (methodology === '360') config.reports?.forEach(id => evaluators.set(id, 'direct_report'));
                        
                        evaluators.forEach((relationship, evaluatorId) => {
                            const responseRef = doc(collection(finalCycleRef, 'responses'));
                            batch.set(responseRef, { cycleId: finalCycleRef.id, evaluatedUserId: evaluatedId, evaluatorUserId: evaluatorId, relationship, status: 'pending', answers: {}, empresaId: adminEmpresaId });
                        });
                    }
                }
                await batch.commit();
                successMessage = newStatus === 'draft' ? 'Rascunho salvo com sucesso!' : 'O ciclo de avaliação foi lançado com sucesso e o alerta correspondente já foi encaminhado por e-mail.';
            }
            
            navigate('/avaliacoes', { state: { message: successMessage, type: 'success' } });

        } catch (error) {
            showNotification('Falha ao salvar ciclo: ' + error.message); 
            console.error(error);
        } finally {
            setIsSaving(false);
        }
    };

    function handleDragEnd(event) {
        const { active, over } = event;
        if (over && active.id !== over.id) {
            setFormQuestions(items => arrayMove(items, items.findIndex(i => i.id === active.id), items.findIndex(i => i.id === over.id)));
        }
    }

    const filteredEvaluatedUsers = useMemo(() => {
        if (!departmentFilter || departmentFilter.value === 'all') return evaluatedUsers;
        return evaluatedUsers.filter(user => user.departamento === departmentFilter.value);
    }, [evaluatedUsers, departmentFilter]);

    if (loading) return <div className="criar-avaliacao-container"><p>Carregando formulário...</p></div>

    return (
        <div className="criar-avaliacao-container">
            <Notification message={notification.message} type={notification.type} onClose={() => setNotification({ message: '', type: '' })} />
            <h2>{pageTitle}</h2>
            
            <div className="form-step">
                <h3>Passo 1: Informações do Ciclo</h3>
                <div ref={cycleNameRef} className={`option-group ${errors.cycleName ? 'has-error' : ''}`}><label className="option-label">Nome do Ciclo</label><input type="text" value={cycleName} onChange={e => setCycleName(e.target.value)} placeholder="Ex: Avaliação Q3 2025" />{errors.cycleName && <span className="error-message">{errors.cycleName}</span>}</div>
                <div className="input-group"><div className="option-group" style={{flex: '2 1 300px'}}><label className="option-label">Metodologia</label><Select options={methodologyOptions} value={methodologyOptions.find(opt => opt.value === methodology)} onChange={(option) => setMethodology(option ? option.value : '360')} styles={sharedSelectStyles} placeholder="Selecione a metodologia..."/></div><div className="option-group" style={{flex: '1 1 150px'}}><label className="option-label">Nota Máxima Final</label><div className="custom-stepper"><button type="button" onClick={() => handleBaseScaleChange(-1)}>-</button><input type="number" value={baseScale} onChange={(e) => setBaseScale(e.target.value)} className="stepper-input" min="1"/><button type="button" onClick={() => handleBaseScaleChange(1)}>+</button></div></div></div>
                <div ref={datesRef} className="date-inputs"><div className={`option-group ${errors.startDate ? 'has-error' : ''}`}><label className="option-label">Início</label><input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} />{errors.startDate && <span className="error-message">{errors.startDate}</span>}</div><div className={`option-group ${errors.endDate ? 'has-error' : ''}`}><label className="option-label">Fim</label><input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} />{errors.endDate && <span className="error-message">{errors.endDate}</span>}</div></div>
                
                <div className="relationship-weights-section">
                    <label className="option-label">Pesos para a Nota Final</label>
                    <div className="weights-grid">
                        {['self', '180', '360'].includes(methodology) && (<div className="option-group"><label>Autoavaliação</label><input type="number" value={relationshipWeights.self} onChange={e => handleWeightChangeForRelationship('self', e.target.value)} min="0" step="0.5" /></div>)}
                        {['180', '360'].includes(methodology) && (<div className="option-group"><label>Gestor</label><input type="number" value={relationshipWeights.manager} onChange={e => handleWeightChangeForRelationship('manager', e.target.value)} min="0" step="0.5" /></div>)}
                        {['peers', '360'].includes(methodology) && (<div className="option-group"><label>Pares</label><input type="number" value={relationshipWeights.peers} onChange={e => handleWeightChangeForRelationship('peers', e.target.value)} min="0" step="0.5" /></div>)}
                        {methodology === '360' && (<div className="option-group"><label>Liderados</label><input type="number" value={relationshipWeights.direct_report} onChange={e => handleWeightChangeForRelationship('direct_report', e.target.value)} min="0" step="0.5" /></div>)}
                    </div>
                </div>

                <div className="nine-box-toggle"><label><input type="checkbox" checked={is9BoxEnabled} onChange={(e) => setIs9BoxEnabled(e.target.checked)}/>Habilitar Análise 9-Box para este ciclo</label><p>A Matriz 9-Box é uma ferramenta visual para identificar talentos, cruzando o Desempenho (eixo X) e o Potencial (eixo Y) dos colaboradores. <a href="https://www.google.com/search?q=matriz+9+box" target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'underline', color: 'var(--primary-color, #007bff)' }}>Saiba mais.</a></p></div>
                
                {/* NOVA SEÇÃO DE VISIBILIDADE DE RESULTADOS */}
                <div className="form-step-subsection">
                    <label className="option-label">Configurações de Visibilidade dos Resultados</label>
                    <p className="subtitle">Defina o que o colaborador avaliado poderá ver ao receber o resultado final.</p>
                    <div className="visibility-grid">
                        <div className="visibility-column">
                            <h5>Notas</h5>
                            <div className="visibility-option">
                                <input type="checkbox" id="showFinalScore" checked={resultsVisibility.showFinalScore} onChange={handleVisibilityChange} />
                                <label htmlFor="showFinalScore">Exibir Nota Final Consolidada</label>
                            </div>
                            <div className="visibility-option">
                                <input type="checkbox" id="showManagerScore" checked={resultsVisibility.showManagerScore} onChange={handleVisibilityChange} disabled={!['180', '360'].includes(methodology)} />
                                <label htmlFor="showManagerScore">Exibir nota detalhada do Gestor</label>
                            </div>
                            <div className="visibility-option">
                                <input type="checkbox" id="showPeerScore" checked={resultsVisibility.showPeerScore} onChange={handleVisibilityChange} disabled={!['peers', '360'].includes(methodology)} />
                                <label htmlFor="showPeerScore">Exibir média das notas dos Pares</label>
                            </div>
                             <div className="visibility-option">
                                <input type="checkbox" id="showSelfScore" checked={resultsVisibility.showSelfScore} onChange={handleVisibilityChange} disabled={!['self', '180', '360'].includes(methodology)} />
                                <label htmlFor="showSelfScore">Exibir nota da Autoavaliação</label>
                            </div>
                        </div>
                        <div className="visibility-column">
                            <h5>Comentários (Justificativas)</h5>
                            <div className="visibility-option">
                                <input type="checkbox" id="showManagerComments" checked={resultsVisibility.showManagerComments} onChange={handleVisibilityChange} disabled={!['180', '360'].includes(methodology)} />
                                <label htmlFor="showManagerComments">Exibir comentários do Gestor</label>
                            </div>
                            <div className="visibility-option">
                                <input type="checkbox" id="showPeerComments" checked={resultsVisibility.showPeerComments} onChange={handleVisibilityChange} disabled={!['peers', '360'].includes(methodology)} />
                                <label htmlFor="showPeerComments">Exibir comentários dos Pares (anônimos)</label>
                            </div>
                        </div>
                    </div>
                </div>

            </div>
            
            <div className="form-step">
                <h3>Passo 2: Selecione os Avaliados</h3>
                <div ref={evaluatedUsersRef} className={`${errors.evaluatedUsers ? 'has-error' : ''}`}>
                    <div className="select-actions"><button onClick={() => handleEvaluatedUsersChange(allUsers)} className="btn-action">Selecionar Todos</button><button onClick={() => handleEvaluatedUsersChange([])} className="btn-action">Limpar Seleção</button></div>
                    <Select isMulti styles={sharedSelectStyles} options={allUsers} value={evaluatedUsers} onChange={(options) => handleEvaluatedUsersChange(options)} isLoading={!allUsers.length && loading} placeholder="Selecione os colaboradores..." noOptionsMessage={() => "Nenhum colaborador encontrado"} />
                    {errors.evaluatedUsers && <span className="error-message">{errors.evaluatedUsers}</span>}
                </div>
            </div>

            {evaluatedUsers.length > 0 && (
                <div className="form-step">
                    <div className="step-header-with-controls">
                        <div className="step-title-group">
                            <h3>Passo 2.5: Revise e Edite os Avaliadores</h3>
                            <p className="subtitle">As sugestões abaixo são baseadas no organograma. Você pode alterá-las como desejar.</p>
                        </div>
                        <div className="controls-wrapper">
                            <div className="department-filter"><Select options={departmentOptions} value={departmentFilter} onChange={setDepartmentFilter} placeholder="Filtrar por Depto..." styles={sharedSelectStyles} isClearable /></div>
                            <div className="view-mode-toggle"><button onClick={() => setViewMode('card')} className={viewMode === 'card' ? 'active' : ''}><FaThLarge /> Cartões</button><button onClick={() => setViewMode('list')} className={viewMode === 'list' ? 'active' : ''}><FaThList /> Lista</button></div>
                        </div>
                    </div>
                    {loadingSuggestions && <p className="loading-text">Buscando sugestões...</p>}
                    {!loadingSuggestions && viewMode === 'card' && filteredEvaluatedUsers.map(user => (<div key={user.id} className="evaluator-card"><h4><FaUserCheck /> Avaliadores de: <strong>{user.label}</strong></h4>{(['180', '360'].includes(methodology)) && ( <div className="evaluator-group"><div className="group-header"><FaUserTie /><label>Gestor(es)</label><span className="suggestion-pills">Sugerido: {finalEvaluatorsConfig[user.id]?.manager?.length || 0}</span></div><Select isMulti styles={sharedSelectStyles} options={allUsers} value={allUsers.filter(u => finalEvaluatorsConfig[user.id]?.manager?.includes(u.id))} onChange={(sel) => handleEvaluatorChange(user.id, 'manager', sel)} /></div>)}{(['peers', '360'].includes(methodology)) && (<div className="evaluator-group"><div className="group-header"><FaUsers /><label>Pares</label><span className="suggestion-pills">Sugeridos: {finalEvaluatorsConfig[user.id]?.peers?.length || 0}</span></div><Select isMulti styles={sharedSelectStyles} options={allUsers} value={allUsers.filter(u => finalEvaluatorsConfig[user.id]?.peers?.includes(u.id))} onChange={(sel) => handleEvaluatorChange(user.id, 'peers', sel)} /></div>)}{(methodology === '360') && (<div className="evaluator-group"><div className="group-header"><FaSitemap /><label>Liderado(s)</label><span className="suggestion-pills">Sugeridos: {finalEvaluatorsConfig[user.id]?.reports?.length || 0}</span></div><Select isMulti styles={sharedSelectStyles} options={allUsers} value={allUsers.filter(u => finalEvaluatorsConfig[user.id]?.reports?.includes(u.id))} onChange={(sel) => handleEvaluatorChange(user.id, 'reports', sel)} /></div>)}</div>))}
                    {!loadingSuggestions && viewMode === 'list' && (<div className="evaluator-list-view"><table><thead><tr><th>Colaborador Avaliado</th>{(['180', '360'].includes(methodology)) && <th>Gestor(es)</th>}{(['peers', '360'].includes(methodology)) && <th>Pares</th>}{(methodology === '360') && <th>Liderado(s)</th>}</tr></thead><tbody>{filteredEvaluatedUsers.map(user => (<tr key={user.id}><td><strong>{user.label}</strong></td>{(['180', '360'].includes(methodology)) && <td><Select isMulti menuPortalTarget={document.body} styles={sharedSelectStyles} options={allUsers} value={allUsers.filter(u => finalEvaluatorsConfig[user.id]?.manager?.includes(u.id))} onChange={(sel) => handleEvaluatorChange(user.id, 'manager', sel)} /></td>}{(['peers', '360'].includes(methodology)) && <td><Select isMulti menuPortalTarget={document.body} styles={sharedSelectStyles} options={allUsers} value={allUsers.filter(u => finalEvaluatorsConfig[user.id]?.peers?.includes(u.id))} onChange={(sel) => handleEvaluatorChange(user.id, 'peers', sel)} /></td>}{(methodology === '360') && <td><Select isMulti menuPortalTarget={document.body} styles={sharedSelectStyles} options={allUsers} value={allUsers.filter(u => finalEvaluatorsConfig[user.id]?.reports?.includes(u.id))} onChange={(sel) => handleEvaluatorChange(user.id, 'reports', sel)} /></td>}</tr>))}</tbody></table></div>)}
                </div>
            )}
            
            <div ref={formQuestionsRef} className="form-step">
                <h3>Passo 3: Monte o Formulário de Avaliação</h3>
                {errors.formQuestions && <span className="error-message" style={{display: 'block', marginBottom: '1rem'}}>{errors.formQuestions}</span>}
                <div className="form-builder">
                    <DndContext collisionDetection={closestCenter} onDragEnd={handleDragEnd}><SortableContext items={formQuestions.map(q => q.id)} strategy={verticalListSortingStrategy}>{formQuestions.map((q, index) => (<SortableQuestionCard key={q.id} id={q.id} q={q} index={index} handleQuestionChange={handleQuestionChange} handleWeightChange={handleWeightChange} deleteQuestion={deleteQuestion} availableScales={availableScales} departmentOptions={departmentOptions} handleMoveQuestion={handleMoveQuestion} isFirst={index === 0} isLast={index === formQuestions.length - 1} is9BoxEnabled={is9BoxEnabled} />))}</SortableContext></DndContext>
                </div>
                <div style={{display: 'flex', gap: '1rem', marginTop: '1.5rem'}}><button onClick={addQuestion} className="btn-add-question"><i className="fas fa-plus"></i> Adicionar Pergunta</button><button onClick={() => setShowScaleManager(true)} className="btn-add-question">Gerenciar Escalas</button></div>
            </div>

            <div className="form-actions">
                <button onClick={() => navigate('/avaliacoes')} className="btn btn-secondary">Cancelar</button>
                {mode !== 'edit' && (<><button onClick={() => handleSaveCycle('draft')} className="btn btn-secondary" disabled={isSaving}>Salvar como Rascunho</button><button onClick={() => handleSaveCycle('active')} className="btn btn-primary" disabled={isSaving}>{isSaving ? 'Salvando...' : 'Lançar Ciclo'}</button></>)}
                {mode === 'edit' && (<>{cycleStatus === 'draft' && (<><button onClick={() => handleSaveCycle('draft')} className="btn btn-secondary" disabled={isSaving}>Salvar Rascunho</button><button onClick={() => handleSaveCycle('active')} className="btn btn-primary" disabled={isSaving}>{isSaving ? 'Lançando...' : 'Lançar Ciclo'}</button></>)}{cycleStatus !== 'draft' && (<button onClick={() => handleSaveCycle(cycleStatus)} className="btn btn-primary" disabled={isSaving}>{isSaving ? 'Salvando...' : 'Salvar Alterações'}</button>)}</>)}
            </div>

            {showScaleManager && (<ScaleManagerModal adminEmpresaId={adminEmpresaId} onClose={() => setShowScaleManager(false)} onScalesUpdate={() => fetchScales(adminEmpresaId)} />)}
        </div>
    );
};

export default CriarAvaliacao;